import { HttpClient } from '@angular/common/http';
import {
    Component,
    DoCheck,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PhoneInputComponent } from 'src/app/module/phone-input/phone-input.component';
import {AgreementType, ChangePasswordStepType} from '../../models/enums';
import { AccountService } from '../../services/account.service';
import { CaptchaComponent } from '../../components/captcha/captcha.component';
import emailMask from 'text-mask-addons/dist/emailMask';
import {MatDialog} from '@angular/material/dialog';
import {RestorePasswordDialog} from '../../module/restore-password-dialog/restore-password-dialog.component';
import { IUserAuthorizationResponse } from '../../models/domain';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy, DoCheck {
    emailMask = emailMask;
    private useSSO = false;
    private _httpClient: HttpClient;
    readonly _dialog: MatDialog;
    constructor(
        @Inject(MatDialog) dialog: MatDialog,
        @Inject(HttpClient) httpClient: HttpClient,
        private _translateService: TranslateService,
        private _router: Router,
        private activatedRoute: ActivatedRoute,
        private accountService: AccountService) {
        this._httpClient = httpClient;
        this._dialog = dialog;
    }

    @ViewChild(PhoneInputComponent) phoneInputComponent: PhoneInputComponent;
    @ViewChild('captchaComponent') captchaComponent: CaptchaComponent;

    public routeParams: {
        ReturnUrl?: string;
    };

    public loginValue: string;
    public passwordValue: string;
    public errorValue: string;
    public showCaptcha: boolean;
    public loginRequestSending: boolean;
    public showLoginForm: boolean;
    public hideAll: boolean = true;
    public clientId: string;
    public captchaCode: string;

    public nornickelLoginFormMode: 'login' | 'phone';
    public nornickelFormHintsObj: {} = null;

    ngOnInit(): void {
        const returnUrl = this.activatedRoute.snapshot.queryParamMap.get('ReturnUrl');
        const decodeReturnUrl = decodeURI(returnUrl);
        const urlParams = new URLSearchParams(decodeReturnUrl.toLowerCase());
        this.useSSO = urlParams.get('use_sso') != null;
        this.clientId = urlParams.get('client_id');

        this.nornickelLoginFormMode = this.activatedRoute.snapshot.queryParamMap.get('loginMode') as ('login' | 'phone') ?? 'login';

        if (this.useSSO) {
            window.location.href = './negotiate?returnUrl=' + encodeURIComponent(returnUrl) + '&clientId=' + encodeURIComponent(this.clientId);
        }

        this.activatedRoute.queryParams.subscribe(params => {
            this.routeParams = params.ReturnUrl
                ? params
                : { ReturnUrl: './login' };
        });

        this._initTranslations();

        this.accountService.isCaptchaNeeded().subscribe(result => this.showCaptcha = result);
    }

    ngDoCheck(): void {
    }

    ngOnDestroy(): void {
    }

    private _initTranslations() {
        this._translateService.get(
            [
                'Common.CaptchaValidationError'
            ])
            .subscribe(
                result => {
                    this.showLoginForm = !!this.clientId && !this.useSSO;
                    this.hideAll = this.useSSO;
                });

        this._translateService.get("Pages.Login.Nornickel.Hint").subscribe((hintsObj) => {
            this.nornickelFormHintsObj = hintsObj;
        })
    }

    private sendLoginByPhoneRequest() {
        this.loginRequestSending = true;

        this.accountService.loginByPhone(
            this.phoneInputComponent.phoneValueWithRegion,
            this.passwordValue,
            this.clientId,
            this.captchaCode)
            .subscribe(
                response => {
                    this.actionAfterLoginResponse(response);
                });
    }

    private sendLoginRequest() {
        this.loginRequestSending = true;

        this.accountService.login(
            this.loginValue,
            this.passwordValue,
            this.clientId,
            this.captchaCode)
            .subscribe(
                response => {
                    this.actionAfterLoginResponse(response);
                });
    }

    private sendLoginByIdapRequest(processedLogin: string) {
        this.loginRequestSending = true;
        this.accountService.loginByIdap(
            processedLogin,
            this.passwordValue,
            this.clientId,
            this.captchaCode
        ).subscribe(
            response => {
                this.actionAfterLoginResponse(response, true);
            }
        )
    }

    public phoneValueChanged() {
        this.errorValue = '';
    }

    public passwordValueChanged() {
        this.errorValue = '';
    }

    public passwordElementOnFocus() {
        this.errorValue = '';
    }

    public doLoginByPhone(event: Event): void {
        event.preventDefault();
        this.sendLoginByPhoneRequest();
    }

    public doLoginByIdap(event: Event): void {
        event.preventDefault();
        let login = this.loginValue;

        if (!this.loginValue.includes('\\') && !this.loginValue.includes('@')) {
            login = `NPR\\${this.loginValue}`
        }

        this.sendLoginByIdapRequest(login);
    }

    public doLogin(event: Event): void {
        event.preventDefault();
        this.sendLoginRequest();
    }

    public doLogout(event: Event): void {
        event.preventDefault();

        this._httpClient.post('./api/v1/Account/Logout', {})
            .subscribe(q => {
                console.log(q);
            });
    }

    public toggleNornickelForm() {
        this.nornickelLoginFormMode = this.nornickelLoginFormMode === 'login' ? 'phone' : 'login';
        this._router.navigate(['/login'], { queryParams: { loginMode: this.nornickelLoginFormMode }, queryParamsHandling: 'merge' });

        this.errorValue = '';
    }

    private actionAfterLoginResponse(response: IUserAuthorizationResponse, isAuthorized: boolean = false) {
        if (response.success) {
            if (response.data.needChangePassword) {

                this._router.navigate(['./changePassword'],
                    {
                        queryParams: {
                            userId: response.data.userId,
                            ticket: response.data.ticket,
                            step: ChangePasswordStepType.EnterNewPassword.toString()
                        },
                        queryParamsHandling: 'merge'
                    });

            } else if (response.data.needUserAgreement || response.data.needUserAgreementSecond) {
                if (response.data.needUserAgreement) {
                    this._router.navigate(['./userAgreementAuthorized'],
                        {
                            queryParams: {
                                agreementType: AgreementType.Main,
                                ticket: response.data.ticket
                            },
                            queryParamsHandling: 'merge'
                        });
                }
                if (response.data.needUserAgreementSecond) {
                    this._router.navigate(['./userAgreementAuthorized'],
                        {
                            queryParams: {
                                agreementType: AgreementType.Second,
                                ticket: response.data.ticket
                            },
                            queryParamsHandling: 'merge'
                        });
                }
            }
            else {
                window.location.href = this.routeParams.ReturnUrl;
            }
        } else {
            if (this.captchaComponent) {
                this.captchaCode = '';
                this.captchaComponent.refreshCaptcha();
            }
        }

        this.loginRequestSending = false;
        this.showCaptcha = response.data.showCaptcha;
        this.errorValue = response.error;
        this.passwordValue = null;
    }

    restorePasswordOpenDialog(){
        this._dialog.open(RestorePasswordDialog)
    }
}
